import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AlertService } from '../../components/alert-service/alert-service';
import { EntitiesEditorComponent } from '../../components/entities-editor/entities-editor.component';
import { ViewerComponent } from '../../components/viewer/viewer.component';
import { CanComponentDeactivate } from '../../interfaces/can-component-deactivate.interface';
import { ReferenceItem, ReferencesService } from '../../services/references.service';
import { PreviewCacheService, PreviewMetadata } from '../../services/preview-cache.service';
import { TenantService } from '../../services/tenant.service';

@Component({
  selector: 'app-reference-detail',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, TranslateModule, EntitiesEditorComponent, ViewerComponent],
  templateUrl: './reference-detail.component.html',
  styleUrls: ['./reference-detail.component.scss']
})
export class ReferenceDetailComponent implements OnInit, CanComponentDeactivate {
  referenceId = '';
  reference: ReferenceItem | null = null;

  predictionData: any = {};
  entities: any[] = [];
  objects: any[] = [];

  selectedEntityId: number | null = null;

  loading = false;
  saving = false;
  hasChanges = false;
  loadError = '';

  previewPages: string[] = [];
  previewMetadata: PreviewMetadata | null = null;
  loadingPreview = false;
  previewError = '';
  viewerImgIndex = 0;

  private originalPredictionSnapshot = '';

  icons = {
    faArrowLeft,
    faSpinner,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private referencesService: ReferencesService,
    private previewCache: PreviewCacheService,
    private alert: AlertService,
    private translate: TranslateService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const referenceId = params.get('referenceId');
      if (!referenceId) {
        this.router.navigate(this.tenantService.buildRoute('/references'));
        return;
      }

      this.referenceId = referenceId;
      this.loadReference(referenceId);
    });
  }

  loadReference(referenceId: string): void {
    this.loading = true;
    this.loadError = '';

    this.referencesService.getReference(referenceId).subscribe({
      next: (response) => {
        this.reference = response.reference;

        const prediction = this.deepClone(this.reference?.data?.['prediction'] || {});
        this.predictionData = prediction;
        this.bindPredictionData();

        const simplified = this.simplifyPrediction();
        this.originalPredictionSnapshot = JSON.stringify(simplified);
        this.hasChanges = false;
        this.loading = false;

        if (this.reference?.source_archive_id) {
          this.loadPreview(this.reference.source_archive_id);
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.loadError = this.translate.instant('REFERENCES.DETAIL_LOAD_ERROR');
        this.alert.showError(err, this.loadError);
      }
    });
  }

  private bindPredictionData(): void {
    if (!this.predictionData) {
      this.entities = [];
      this.objects = [];
      return;
    }

    let rawEntities: any[] = [];
    let rawObjects: any[] = [];

    if (this.predictionData.result && Array.isArray(this.predictionData.result.entities)) {
      this.entities = this.predictionData.result.entities;
      this.objects = this.predictionData.result.objects || [];
    } else if (Array.isArray(this.predictionData.entities)) {
      this.entities = this.predictionData.entities;
      this.objects = this.predictionData.objects || [];
    } else {
      this.entities = [];
      this.objects = [];

      Object.keys(this.predictionData).forEach(key => {
        const val = this.predictionData[key];
        if (val === null || val === undefined) return;
        
        if (Array.isArray(val)) {
          if (val.length > 0 && typeof val[0] === 'object' && val[0] !== null) {
            val.forEach(objItem => {
              const transformed = this.transformFlatToObj(key, objItem);
              if (Object.keys(transformed.values).length > 0) {
                this.objects.push(transformed);
              }
            });
          } else {
            val.forEach(text => {
              this.entities.push({
                id: 'gen_' + Math.random().toString(36).substr(2, 9),
                label: key,
                text: String(text),
                probability: 1.0
              });
            });
          }
        } else if (typeof val === 'object' && val !== null) {
          const transformed = this.transformFlatToObj(key, val);
          if (Object.keys(transformed.values).length > 0) {
            this.objects.push(transformed);
          } else {
             this.entities.push({
                id: 'gen_' + Math.random().toString(36).substr(2, 9),
                label: key,
                text: JSON.stringify(val),
                probability: 1.0
              });
          }
        } else {
          this.entities.push({
            id: 'gen_' + Math.random().toString(36).substr(2, 9),
            label: key,
            text: String(val),
            probability: 1.0
          });
        }
      });
    }
  }

  private transformFlatToObj(relation: string, flatValues: any): any {
    const values: { [key: string]: any[] } = {};
    Object.keys(flatValues).forEach(k => {
      const v = flatValues[k];
      if (Array.isArray(v)) {
        values[k] = v.map(text => ({
          id: 'gen_' + Math.random().toString(36).substr(2, 9),
          text: String(text),
          relation_score: 1.0
        }));
      } else {
        values[k] = [{
          id: 'gen_' + Math.random().toString(36).substr(2, 9),
          text: String(v),
          relation_score: 1.0
        }];
      }
    });

    return {
      relation: relation,
      values: values
    };
  }

  onEntityClick(entityId: number): void {
    this.selectedEntityId = this.selectedEntityId === entityId ? null : entityId;
  }

  onContentChanged(): void {
    const simplified = this.simplifyPrediction();
    this.hasChanges = JSON.stringify(simplified) !== this.originalPredictionSnapshot;
  }

  onSave(): void {
    if (!this.referenceId || this.saving) {
      return;
    }

    this.saving = true;

    const simplifiedData = this.simplifyPrediction();

    this.referencesService.updateReference(this.referenceId, {
      prediction: simplifiedData
    }).subscribe({
      next: () => {
        this.saving = false;
        this.originalPredictionSnapshot = JSON.stringify(simplifiedData);
        this.hasChanges = false;
        this.alert.showSuccess(this.translate.instant('REFERENCES.SAVE_SUCCESS'));
      },
      error: (err: any) => {
        this.saving = false;
        this.alert.showError(err, this.translate.instant('REFERENCES.SAVE_ERROR'));
      }
    });
  }

  private simplifyPrediction(): any {
    const kvData: { [key: string]: any } = {};

    if (this.entities && Array.isArray(this.entities)) {
      this.entities.forEach(entity => {
        if (entity.label && entity.text !== undefined) {
          if (kvData[entity.label]) {
            if (Array.isArray(kvData[entity.label])) {
              kvData[entity.label].push(entity.text);
            } else {
              kvData[entity.label] = [kvData[entity.label], entity.text];
            }
          } else {
            kvData[entity.label] = entity.text;
          }
        }
      });
    }

    if (this.objects && Array.isArray(this.objects)) {
      this.objects.forEach(obj => {
        if (obj.relation && obj.values) {
          const objValues: { [key: string]: any } = {};
          Object.keys(obj.values).forEach(key => {
            const valArray = obj.values[key];
            if (Array.isArray(valArray)) {
              objValues[key] = valArray.map((v: any) => v.text);
              if (objValues[key].length === 1) objValues[key] = objValues[key][0];
            }
          });

          if (kvData[obj.relation]) {
            if (Array.isArray(kvData[obj.relation])) {
              kvData[obj.relation].push(objValues);
            } else {
              kvData[obj.relation] = [kvData[obj.relation], objValues];
            }
          } else {
            kvData[obj.relation] = objValues;
          }
        }
      });
    }

    return kvData;
  }

  onBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/references'));
  }

  loadPreview(archiveId: string): void {
    this.loadingPreview = true;
    this.previewError = '';
    this.previewPages = [];
    this.previewMetadata = null;
    
    this.loadPreviewBatch(archiveId, 1);
  }

  private loadPreviewBatch(archiveId: string, page: number): void {
    this.previewCache.getPreviewPages(archiveId, page).subscribe({
      next: ({ pages, metadata }) => {
        if (page === 1) {
          this.previewPages = pages.map(p => p.base64Data);
        } else {
          this.previewPages = [...this.previewPages, ...pages.map(p => p.base64Data)];
        }
        this.previewMetadata = metadata;
        this.loadingPreview = false;
      },
      error: (error: any) => {
        this.loadingPreview = false;
        this.previewError = error.error?.message || 'ERROR.LOADING.PREVIEW';
      }
    });
  }

  onPreviewPageChange(newIndex: number): void {
    this.viewerImgIndex = newIndex;
    
    if (!this.previewMetadata || !this.reference?.source_archive_id) return;

    const loadedPages = this.previewPages.length;
    const requestedPageNumber = newIndex + 1;

    if (requestedPageNumber > loadedPages) {
      const nextBatchPage = Math.ceil(requestedPageNumber / this.previewMetadata.pageSize);
      this.loadPreviewBatch(this.reference.source_archive_id, nextBatchPage);
    }
  }

  get saveLabel(): string {
    const saveText = this.translate.instant('BUTTON.SAVE');
    return this.hasChanges ? `* ${saveText}` : saveText;
  }

  canDeactivate(): boolean {
    if (!this.hasChanges) {
      return true;
    }

    return confirm(this.translate.instant('REFERENCES.UNSAVED_CHANGES_CONFIRM'));
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent): void {
    if (!this.hasChanges) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  private serializePrediction(): string {
    return JSON.stringify(this.simplifyPrediction());
  }

  private deepClone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
